import { Platform } from '@angular/cdk/platform';
import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable, SecurityContext } from "@angular/core";
import { DomSanitizer } from '@angular/platform-browser';
import { from, Observable } from "rxjs";
import { concatMap, map, switchMap, tap } from "rxjs/operators";
import { FileType, UploadProgress, UploadResponse } from '../models';

export enum UploadEndpoint {
    BusinessImage,
    BusinessFile,
    UserProfileImage,
    BusinessLogo,
    ChannelBusinessImage,
    ChannelLogo
}

@Injectable({
    providedIn: 'root'
})
export class UploadService {
    private _chunkSize = 4194304;

    constructor(private http: HttpClient, public platform: Platform, private sanitizer: DomSanitizer) {
    };

    upload(file: File, type: FileType, endpoint: UploadEndpoint, hubId?: number, businessId?: number, folderId?: number, channelId?: number): Observable<UploadProgress> {
        var chunks: number[] = new Array(Math.ceil(file.size / this._chunkSize));
        var currentChunk = 0;
        var identifier = this.uuidv4();
        var params = new HttpParams();

        var endpointPath: string;
        if (endpoint === UploadEndpoint.BusinessImage) {
            endpointPath = "/admin/images/hub/" + hubId + "/upload";
            if (folderId && folderId !== 0) {
                params = params.set('folderId', folderId.toString());
            }
        }
        else if (endpoint === UploadEndpoint.ChannelLogo) {
          endpointPath = "/admin/channels/" + channelId + "/eventapp/file/upload?";
        }
        else if (endpoint === UploadEndpoint.BusinessLogo) {
            endpointPath = "/admin/images/hub/" + hubId + "/upload";
            params = params.set('typeId', '1');
        }
        else if (endpoint === UploadEndpoint.BusinessFile) {
            endpointPath = "/admin/hub/" + hubId + "/business/" + businessId + "/file/upload?";
        }
        else if (endpoint === UploadEndpoint.UserProfileImage) {
            endpointPath = "/user/me/profileimage?";
        }
        else if (endpoint == UploadEndpoint.ChannelBusinessImage) {
            endpointPath = "/admin/channels/" + channelId + "/hub/" + hubId + "/upload";
        }

        if (params.keys().length > 0) {
            endpointPath = endpointPath + "?" + params.toString() + "&";
        }
        else {
            endpointPath = endpointPath + "?";
        }

        return this.autoRotate(file).pipe(
            tap(autoRotate => {
                if (endpoint !== UploadEndpoint.BusinessFile) {
                    endpointPath += "autoRotate=" + (autoRotate ? "true" : "false") + "&";
                }
            }),
            switchMap(() => from(chunks)),
            concatMap((foo, chunk) => {
                currentChunk = chunk;
                let start = chunk * this._chunkSize;
                let end = start + this._chunkSize;
                let data = file.slice(start, end, file.type);

                let url = endpointPath + "chunk=" + chunk.toString() + "&chunkLength=" + chunks.length + "&identifier=" + identifier + "&filename=" + file.name + "&type=" + type.valueOf();
                let formData = new FormData();
                formData.append("file", data);

                return this.http.post<UploadResponse>(url, formData, { reportProgress: true, observe: 'events' });
            }),
            map(event => {
                if (event.type === HttpEventType.Response) {
                    event.body.fileName = file.name;
                    return { status: 'complete', progress: 100, fileName: file.name, body: event.body };
                }
                else if (event.type === HttpEventType.UploadProgress) {
                    let loaded = currentChunk * this._chunkSize + event.loaded;
                    const progress = Math.round(100 * loaded / file.size);
                    return { status: 'progress', progress: progress, fileName: file.name, body: null };
                }
            })
        );
    }

    /*
    iOS workaround. On iOS if user chooses to resize (in ios gui) before upload 
    the browser rotates the image but removes exif metadata.
    Workaround is to check image resolution to see if it's smaller than maximum 
    camera resolution. This can fail for many reasons, image taken with front camera
    or image not taken using camera at all for example.
    Camera resolutions: https://developer.apple.com/library/archive/documentation/DeviceInformation/Reference/iOSDeviceCompatibility/Cameras/Cameras.html
    */
    private autoRotate(file: File): Observable<boolean> {
        return new Observable<boolean>(o => {            
            if (this.platform.IOS) {
                const img = document.createElement("img");
                img.src = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(file)));
                img.onload = () => {
                    const autoRotate = img.width >= 3264;
                    o.next(autoRotate);
                    o.complete();
                };
                img.onerror = () => {
                    o.next(true);
                    o.complete();
                }
            }
            else {
                o.next(true);
                o.complete();
            }
        });
    }

    private uuidv4() {
        return (1e7 + '-' + 1e3 + '-' + 4e3 + '-' + 8e3 + '-' + 1e11).replace(/[018]/g, s => {
            let c = parseInt(s);
            return (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16);
        });
    }
}